import { flow } from "@prosperstack/flow";
import {
  AlertDialog,
  AlertDialogContent,
  Separator,
} from "@pushpress/pushpress-ui";
import { ChevronRightCircleIcon, XIcon } from "lucide-react";
import {
  allStripeSubscriptionInfo,
  getSubscriptionInfo,
} from "../constants/subscriptionInfoMap";
import { CustomerStripeSubscriptionSummary } from "../../../apis/billingApi";
import { CORE_STRIPE_PRICE_IDS } from "../constants/subscriptionIds";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { AccountEvent } from "../constants/account.events";

interface AccountCancellationModalProps {
  showCancelOrDowngradePlan: boolean;
  setShowCancelOrDowngradePlan: (show: boolean) => void;
  subscriptionSummaries: CustomerStripeSubscriptionSummary[];
}

const AccountCancellationModal: React.FC<AccountCancellationModalProps> = ({
  showCancelOrDowngradePlan,
  setShowCancelOrDowngradePlan,
  subscriptionSummaries = [],
}) => {
  const { trackEvent } = useAnalytics();
  const callFlow = (
    flowId: string,
    subscriptionId: string,
    productName: string,
  ) => {
    setShowCancelOrDowngradePlan(false);
    trackEvent(AccountEvent.PlanCancellationAttempted, {
      location: "Account Page",
      subSection: "Account cancellation modal",
      product: productName,
    });
    flow({
      clientId: process.env.PROSPER_STACK_CLIENT_ID,
      flowId: flowId,
      subscription: {
        platformId: subscriptionId,
      },
    });
  };

  return (
    <AlertDialog
      open={showCancelOrDowngradePlan}
      onOpenChange={setShowCancelOrDowngradePlan}
    >
      <AlertDialogContent>
        <div className="tw-flex tw-flex-col">
          <div className="tw-flex tw-items-center tw-justify-between tw-pb-5">
            <XIcon
              onClick={() => setShowCancelOrDowngradePlan(false)}
              className="tw-cursor-pointer"
            />
            <p className="tw-text-xl">Cancel or downgrade plan</p>
            {/* Placeholder div used to balance modal title */}
            <div />
          </div>
          <Separator
            className="tw-mb-4 tw-w-[512px] tw-mx-[-25px]"
            orientation="horizontal"
          />

          {subscriptionSummaries.map((activeSubscription) => {
            const planInfo = getSubscriptionInfo(
              allStripeSubscriptionInfo,
              activeSubscription.priceId,
            );

            if (!planInfo) {
              trackEvent(AccountEvent.PlanInformationNotFound, {
                priceId: activeSubscription.priceId,
                location: "Account Page",
                subSection: "Account cancellation modal",
              });

              return null;
            }

            const cancellationCta = CORE_STRIPE_PRICE_IDS.includes(
              activeSubscription.priceId,
            )
              ? "Cancel or downgrade"
              : "Cancel";

            return (
              <div
                className="tw-flex tw-flex-col tw-gap-3 tw-mt-5 tw-cursor-pointer"
                key={activeSubscription.priceId}
              >
                <div
                  onClick={() =>
                    callFlow(
                      planInfo.prosperStackCancellationFlowId,
                      activeSubscription.subscriptionId,
                      planInfo.title ?? "",
                    )
                  }
                  onKeyDown={() =>
                    callFlow(
                      planInfo.prosperStackCancellationFlowId,
                      activeSubscription.subscriptionId,
                      planInfo.title ?? "",
                    )
                  }
                  role="button"
                  tabIndex={0}
                  className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-5 tw-border tw-border-neutral-200 tw-rounded-sm tw-cursor-pointer"
                >
                  <div className="tw-text-neutral-800 tw-text-[16px]">
                    {cancellationCta} {planInfo.title}
                  </div>
                  <div className="tw-text-neutral-500">
                    <ChevronRightCircleIcon size={36} strokeWidth={1} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default AccountCancellationModal;
